import ScriptComponentLoader from './ScriptComponentLoader'
import PageTransition from '@views/elements/e-PageTransition/e-PageTransition'
import NewsletterPopup from '@views/elements/e-NewsletterPopup/e-NewsletterPopup'
import BarbaBodyHelper from './BarbaBodyHelper'
import Header from '../../../views/elements/e-Header/e-Header'
import gsap from 'gsap'

/**
 * Automatically import all scripts and create a map
 */
const requireComponent = require.context(
	'@views', // The directory to search in
	true, // Search subdirectories
	/\/(s-|c-|e-).+\.js$/ // Regex to match the files
)

const scriptsMap = {}

requireComponent.keys().forEach((fileName) => {
	// Get the component config
	const componentConfig = requireComponent(fileName)

	// Extract the name of the component
	const componentName = fileName
		.replace(/^.*[\\/]/, '') // Remove the leading directory path
		.replace(/\.[^/.]+$/, '') // Remove the file extension

	// Convert to camelCase if needed
	const camelCaseName = componentName.replace(/-([a-z])/g, (g) => g[1].toUpperCase())

	// Add the component to the scriptsMap
	scriptsMap[camelCaseName] = componentConfig.default || componentConfig
})

export default class Page {
	constructor(config = {}) {
		this.scriptRefs = []
		this.hasTransitions = config.hasTransitions

		this.scriptComponentLoader = new ScriptComponentLoader()
		this.bodyHelper = new BarbaBodyHelper()

		this.header = new Header(document.querySelector('.e-Header'))

		if (this.hasTransitions) {
			this.pageTransition = new PageTransition(document.querySelector('.e-PageTransition'))
		}

		this.setupNewsletterPopup()
	}

	setupNewsletterPopup() {
		const newsletterPopupElement = document.querySelector('.e-NewsletterPopup')

		if (!newsletterPopupElement) {
			if (this.newsletterPopup && this.newsletterPopup.unmount) {
				this.newsletterPopup.unmount()
			}
			this.newsletterPopup = null
			return
		}

		if (this.newsletterPopup && this.newsletterPopup.element === newsletterPopupElement) {
			return
		}

		if (this.newsletterPopup && this.newsletterPopup.unmount) {
			this.newsletterPopup.unmount()
		}

		this.newsletterPopup = new NewsletterPopup(newsletterPopupElement)
	}

	init() {
		this.scriptRefs = []

		this.scriptComponentLoader.getAndLoad(this.scriptRefs, scriptsMap)
		this.setupSliders()
		this.setupFileUploader()
	}

	resetAllSliders() {
		document.body.classList.remove('wholesale-open')
		document.body.classList.remove('login-open')
		document.body.classList.remove('custom-enquiry-open')
		document.body.classList.remove('contact-open')
	}

	onClickWholesale(e) {
		e.preventDefault()
		e.stopPropagation()
		this.resetAllSliders()
		document.body.classList.add('wholesale-open')
	}

	onClickCustomEnquiry(e) {
		e.preventDefault()
		e.stopPropagation()
		this.resetAllSliders()
		document.body.classList.add('custom-enquiry-open')
	}

	onClickContactForm(e) {
		e.preventDefault()
		e.stopPropagation()
		this.resetAllSliders()
		document.body.classList.add('contact-open')
	}

	onClickNewsletter(e) {
		e.preventDefault()
		e.stopPropagation()
		document.body.classList.add('newsletter-open')
	}

	onClickLogin(e) {
		e.stopPropagation()
		e.preventDefault()
		this.resetAllSliders()
		document.body.classList.add('login-open')
	}

	onClickSearch(e) {
		e.stopPropagation()
		e.preventDefault()
		this.resetAllSliders()
		document.body.classList.add('search-open')
		const searchInput = document.querySelector('.s-Search__form input')
		if (searchInput) {
			// console.log('focussing!')
			searchInput.focus()
		}
	}

	setupSliders() {
		this.setupWholesaleSliders()
		this.setupCustomEnquirySliders()
		this.setupNewsletter()
		this.setupLoginSliders()
		this.setupContactFormSliders()
		this.setupSearchSliders()
	}

	setupFileUploader() {
		const fileInputs = gsap.utils.toArray('input[type="file"]')

		fileInputs.forEach((input) => {
			const label = input.previousElementSibling
			label.classList.add('file-label')
			const div = document.createElement('div')
			div.classList.add('file-label__label')
			label.appendChild(div)
			input.addEventListener('change', this.onChangeInput.bind(this))
		})
	}

	onChangeInput(e) {
		const fileInput = e.target
		const label = fileInput.previousElementSibling
		const labelInner = label.querySelector('.file-label__label')

		if (fileInput.files.length > 0) {
			if (labelInner) {
				label.classList.add('active')
				labelInner.innerText = fileInput.files[0].name
			}
		} else {
			label.classList.remove('active')
			labelInner.innerText = ''
		}
	}

	setupCustomEnquirySliders() {
		this.customEnquiryTriggers = [...document.querySelectorAll('.trigger-custom')]
		this.customEnquiryTriggers.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.addEventListener('click', this.onClickCustomEnquiry.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.addEventListener('click', this.onClickCustomEnquiry.bind(this))
			}
		})
	}

	setupNewsletter() {
		this.newsletterTriggers = [...document.querySelectorAll('.trigger-newsletter')]
		this.newsletterTriggers.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.addEventListener('click', this.onClickNewsletter.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.addEventListener('click', this.onClickNewsletter.bind(this))
			}
		})
	}

	setupContactFormSliders() {
		this.contactFormTriggers = [...document.querySelectorAll('.trigger-contact')]
		this.contactFormTriggers.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.addEventListener('click', this.onClickContactForm.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.addEventListener('click', this.onClickContactForm.bind(this))
			}
		})
	}

	destroyContactFormSliders() {
		if (this.contactFormTriggers) {
			this.contactFormTriggers.forEach((el) => {
				if (el.nodeName == 'LI') {
					const link = el.querySelector('a')
					if (link) {
						link.removeEventListener('click', this.onClickContactForm.bind(this))
					}
				} else if (el.nodeName == 'A') {
					el.removeEventListener('click', this.onClickContactForm.bind(this))
				}
			})
		}
	}

	destroyCustomEnquirySliders() {
		if (this.wholesaleTriggers) {
			this.wholesaleTriggers.forEach((el) => {
				if (el.nodeName == 'LI') {
					const link = el.querySelector('a')
					if (link) {
						link.removeEventListener('click', this.onClickCustomEnquiry.bind(this))
					}
				} else if (el.nodeName == 'A') {
					el.removeEventListener('click', this.onClickCustomEnquiry.bind(this))
				}
			})
		}
	}

	setupLoginSliders() {
		this.loginTriggers = [...document.querySelectorAll('.trigger-trade')]
		this.loginTriggers.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.addEventListener('click', this.onClickLogin.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.addEventListener('click', this.onClickLogin.bind(this))
			}
		})
	}
	setupSearchSliders() {
		this.searchSliders = [...document.querySelectorAll('.trigger-search')]
		this.searchSliders.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.addEventListener('click', this.onClickSearch.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.addEventListener('click', this.onClickSearch.bind(this))
			}
		})
	}

	destroySearchSliders() {
		this.searchSliders = [...document.querySelectorAll('.trigger-search')]
		this.searchSliders.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.removeEventListener('click', this.onClickSearch.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.removeEventListener('click', this.onClickSearch.bind(this))
			}
		})
	}

	destroyLoginSliders() {
		if (this.loginTriggers) {
			this.loginTriggers.forEach((el) => {
				if (el.nodeName == 'LI') {
					const link = el.querySelector('a')
					if (link) {
						link.removeEventListener('click', this.onClickLogin.bind(this))
					}
				} else if (el.nodeName == 'A') {
					el.removeEventListener('click', this.onClickLogin.bind(this))
				}
			})
		}
	}

	setupWholesaleSliders() {
		this.wholesaleTriggers = [...document.querySelectorAll('.trigger-wholesale')]
		this.wholesaleTriggers.forEach((el) => {
			if (el.nodeName == 'LI') {
				const link = el.querySelector('a')
				if (link) {
					link.addEventListener('click', this.onClickWholesale.bind(this))
				}
			} else if (el.nodeName == 'A') {
				el.addEventListener('click', this.onClickWholesale.bind(this))
			}
		})
	}
	destroyWholesaleSliders() {
		if (this.wholesaleTriggers) {
			this.wholesaleTriggers.forEach((el) => {
				if (el.nodeName == 'LI') {
					const link = el.querySelector('a')
					if (link) {
						link.removeEventListener('click', this.onClickWholesale.bind(this))
					}
				} else if (el.nodeName == 'A') {
					el.removeEventListener('click', this.onClickWholesale.bind(this))
				}
			})
		}
	}

	destroySliders() {
		this.destroyWholesaleSliders()
		this.destroyCustomEnquirySliders()
		this.destroyLoginSliders()
		this.destroyContactFormSliders()
		this.destroySearchSliders()
	}

	mountScripts(data) {
		this.setupSliders()
		this.setupNewsletterPopup()
		return new Promise((resolve) => {
			this.scriptRefs = []

			this.scriptComponentLoader.filterAndLoad(this.scriptRefs, scriptsMap, data)

			resolve()
		})
	}

	unmountScripts(data) {
		this.destroySliders()

		if (this.newsletterPopup && this.newsletterPopup.unmount) {
			this.newsletterPopup.unmount()
			this.newsletterPopup = null
		}

		this.bodyHelper.getClassesAndDataAttributes(data)

		return new Promise((resolve) => {
			this.scriptComponentLoader.unmountComponents(this.scriptRefs, data)

			resolve()
		})
	}

	genericPageOnce() {
		this.pageTransition.once()
	}

	genericPageLeave() {
		return new Promise((resolve) => {
			this.pageTransition.leave(resolve)
		})
	}

	genericPageAfter() {
		this.pageTransition.after()
		this.header.highlightActiveLink()
	}

	resize() {
		if (this.scriptRefs) {
			this.scriptRefs.forEach((component) => {
				if (component.resize) {
					component.resize()
				}
			})
		}
	}

	update(dt) {
		if (this.scriptRefs) {
			this.scriptRefs.forEach((component) => {
				if (component.update) {
					component.update(dt)
				}
			})
		}
	}
}
